import { useMemo } from 'react';
import { useField } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';

import { useWaypointTemplateQuery } from '@dashboard/generated/wp/platform';
import { useCurrentScenarioName } from '@wp-block-builder/editor/hooks';

import { useCurrentWaypointTemplateQuery } from '../useInitialValues';

type TScenario = { name: string; variables: string };
export function useScenariosMutator() {
  return useFieldArray<TScenario>('testScenarios').fields;
}

export function useCurrentPreviewVariablesFieldName() {
  const [name] = useCurrentScenarioName();
  return name ? `${name}.variables` : 'defaultPreviewVariables';
}

export function useCurrentPreviewVariables() {
  const variablesPath = useCurrentPreviewVariablesFieldName();
  return useField<string>(variablesPath);
}

export function useCurrentPreviewVariablesObject() {
  const {
    input: { value },
  } = useCurrentPreviewVariables();

  return useMemo(() => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return {};
    }
  }, [value]);
}

export function useCurrentTemplateType(): 'LAYOUT' | 'TEMPLATE' | null {
  const { data } = useCurrentWaypointTemplateQuery();
  return data?.WaypointTemplate.type ?? null;
}

export function useCurrentLayoutConfiguration() {
  const layoutId = useCurrentLayoutId();
  const { data, loading } = useWaypointTemplateQuery({
    variables: { id: layoutId ?? '' },
    skip: layoutId === null,
  });
  if (loading) {
    return { loading: true, data: null };
  }
  if (data === undefined) {
    return { loading: false, data: null };
  }
  return { loading: false, data: data.WaypointTemplate.activeConfiguration };
}

function useCurrentLayoutId() {
  const layoutId = useField<string>('configuration.layoutId').input.value;
  if (typeof layoutId === 'string' && layoutId.trim().length > 0) {
    return layoutId;
  }
  return null;
}
